import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { CustomResponseEntity, PaginationParams } from '../types/common';
import {
  CreateIdTypeDto,
  CreatePrivilegeDto,
  CreateRoleDto,
  CreateUserDto,
  IdTypeDto,
  PrivilegeDto,
  RoleDto,
  UpdateIdTypeDto,
  UpdatePrivilegeDto,
  UpdateRoleDto,
  UpdateUserDto,
  UserDto,
} from '../types/dtos';
import { idTypeService, privilegeService, roleService, userService } from '../services/user.service';

// Query keys
export const userKeys = {
  all: ['user'] as const,
  users: () => [...userKeys.all, 'list'] as const,
  user: (id: string) => [...userKeys.users(), id] as const,
  roles: () => [...userKeys.all, 'roles'] as const,
  role: (id: string) => [...userKeys.roles(), id] as const,
  privileges: () => [...userKeys.all, 'privileges'] as const,
  privilege: (id: string) => [...userKeys.privileges(), id] as const,
  idTypes: () => [...userKeys.all, 'id-types'] as const,
  idType: (id: string) => [...userKeys.idTypes(), id] as const,
};

// Users
export const useUsers = (params?: PaginationParams, options?: UseQueryOptions<CustomResponseEntity<UserDto[]>>) => {
  return useQuery({
    queryKey: userKeys.users(),
    queryFn: () => userService.findAll(params),
    ...options,
  });
};

export const useUser = (id: string, options?: UseQueryOptions<CustomResponseEntity<UserDto>>) => {
  return useQuery({
    queryKey: userKeys.user(id),
    queryFn: () => userService.findById(id),
    ...options,
  });
};

export const useCreateUser = () => {
  return useMutation({
    mutationFn: (data: CreateUserDto) => userService.create(data),
  });
};

export const useUpdateUser = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserDto }) => userService.update(id, data),
  });
};

export const useDeleteUser = () => {
  return useMutation({
    mutationFn: (id: string) => userService.delete(id),
  });
};

// Roles
export const useRoles = (params?: PaginationParams, options?: UseQueryOptions<CustomResponseEntity<RoleDto[]>>) => {
  return useQuery({
    queryKey: userKeys.roles(),
    queryFn: () => roleService.findAll(params),
    ...options,
  });
};

export const useRolesByOrganization = (organizationId: string, options?: UseQueryOptions<CustomResponseEntity<RoleDto[]>>) => {
  return useQuery({
    queryKey: [...userKeys.roles(), 'byOrganization', organizationId],
    queryFn: () => roleService.findByOrganization(organizationId),
    enabled: !!organizationId,
    ...options,
  });
};

// Admin hook to get all roles without organization context
export const useAdminAllRoles = (options?: UseQueryOptions<CustomResponseEntity<RoleDto[]>>) => {
  return useQuery({
    queryKey: [...userKeys.roles(), 'admin', 'all'],
    queryFn: async () => {
      const { api } = await import('../client/axiosClient');
      const { ENDPOINTS } = await import('../endpoints');
      const response = await api.get(ENDPOINTS.roles.base());
      return (response as any).data;
    },
    ...options,
  });
};

// Admin hook to get user roles without organization context
export const useAdminUserRoles = (userId: string, options?: UseQueryOptions<CustomResponseEntity<any[]>>) => {
  return useQuery({
    queryKey: [...userKeys.roles(), 'admin', 'user', userId],
    queryFn: async () => {
      const { api } = await import('../client/axiosClient');
      const { ENDPOINTS } = await import('../endpoints');
      
      
      // 1. Obtener los userRoles del usuario
      const userRolesResponse = await api.get(ENDPOINTS.admin.userRoles(userId));
      const userRoles = (userRolesResponse as any).data;
      
      
      // 2. Obtener todos los roles para mapear la información completa
      const allRolesResponse = await api.get(ENDPOINTS.roles.base());
      const allRoles = (allRolesResponse as any).data;
      
      
      // 3. Mapear los userRoles con la información completa del role
      const userRolesWithRoleInfo = userRoles.map((userRole: any) => {
        const roleInfo = allRoles.find((role: any) => role.id === userRole.roleId);
        return {
          ...userRole,
          role: roleInfo
        };
      });
      
      
      return userRolesWithRoleInfo;
    },
    enabled: !!userId,
    ...options,
  });
};

export const useRole = (id: string, options?: UseQueryOptions<CustomResponseEntity<RoleDto>>) => {
  return useQuery({
    queryKey: userKeys.role(id),
    queryFn: () => roleService.findById(id),
    ...options,
  });
};

export const useCreateRole = () => {
  return useMutation({
    mutationFn: (data: CreateRoleDto) => roleService.create(data),
  });
};

export const useUpdateRole = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleDto }) => roleService.update(id, data),
  });
};

export const useDeleteRole = () => {
  return useMutation({
    mutationFn: (id: string) => roleService.delete(id),
  });
};

// Privileges
export const usePrivileges = (params?: PaginationParams, options?: UseQueryOptions<CustomResponseEntity<PrivilegeDto[]>>) => {
  return useQuery({
    queryKey: userKeys.privileges(),
    queryFn: () => privilegeService.findAll(params),
    ...options,
  });
};

export const usePrivilege = (id: string, options?: UseQueryOptions<CustomResponseEntity<PrivilegeDto>>) => {
  return useQuery({
    queryKey: userKeys.privilege(id),
    queryFn: () => privilegeService.findById(id),
    ...options,
  });
};

export const useCreatePrivilege = () => {
  return useMutation({
    mutationFn: (data: CreatePrivilegeDto) => privilegeService.create(data),
  });
};

export const useUpdatePrivilege = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePrivilegeDto }) => privilegeService.update(id, data),
  });
};

export const useDeletePrivilege = () => {
  return useMutation({
    mutationFn: (id: string) => privilegeService.delete(id),
  });
};

// ID Types
export const useIdTypes = (params?: PaginationParams, options?: UseQueryOptions<CustomResponseEntity<IdTypeDto[]>>) => {
  return useQuery({
    queryKey: userKeys.idTypes(),
    queryFn: () => idTypeService.findAll(params),
    ...options,
  });
};

export const useIdType = (id: string, options?: UseQueryOptions<CustomResponseEntity<IdTypeDto>>) => {
  return useQuery({
    queryKey: userKeys.idType(id),
    queryFn: () => idTypeService.findById(id),
    ...options,
  });
};

export const useCreateIdType = () => {
  return useMutation({
    mutationFn: (data: CreateIdTypeDto) => idTypeService.create(data),
  });
};

export const useUpdateIdType = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateIdTypeDto }) => idTypeService.update(id, data),
  });
};

export const useDeleteIdType = () => {
  return useMutation({
    mutationFn: (id: string) => idTypeService.delete(id),
  });
};
